import { Color } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons';
import { isEqual } from 'lodash/fp';
import { StylesConfig } from 'react-select';
import styled from 'styled-components';

import FormControlPilledInput from 'dashboard/components/FormControlPilledInput';
import { Control, Value } from 'dashboard/components/FormControlPilledInput/types';
import { operatorDisplayValue } from 'dashboard/components/GraphQueryEditor/lib';
import ReactSelect from 'dashboard/components/ReactSelect';
import {
  Conditional,
  isConditionalValid,
  Operator,
  QueryConditionalConfig,
  QueryConditionalLeft,
} from 'dashboard/models/graph-query';
import Input from 'design-system/components/Input';

const Row = styled.div`
  display: contents;
  margin-bottom: 12px;
  align-items: center;
`;

const LabelContainer = styled.div<{ connectedBy?: boolean }>`
  grid-column: ${({ connectedBy }) => {
    if (connectedBy) {
      return '1 / 5';
    } else {
      return '1 / 2';
    }
  }};
`;

// height matches that of inputMixin that Select uses
const Label = styled.div`
  display: flex;
  height: 32px;
  align-items: center;
  user-select: none;
`;

const CriterionContainer = styled.div<{ connectedBy?: boolean }>`
  grid-column: ${({ connectedBy }) => {
    if (connectedBy) {
      return '5 / 6';
    } else {
      return '2 / 5';
    }
  }};
`;

const OperatorContainer = styled.div`
  grid-column: '5 / 6';
`;

const ValueContainer = styled.div<{ canRemove: boolean }>`
  grid-column: ${({ canRemove }) => (canRemove ? '6 / 7' : '6 / 8')};
`;

const RemoveRow = styled.div`
  grid-column: 7 / 8;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const RemoveRowIcon = styled(XIcon)`
  height: 16px;
  width: 16px;
`;

const reactSelectStyles: StylesConfig = {
  singleValue: (styles) => ({
    ...styles,
    color: Color.CoreSlate1000,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '125%',
  }),
  control: () => ({
    height: '32px',
  }),
  valueContainer: () => ({
    paddingTop: '0',
  }),
};

const ConditionalRow = ({
  connectedBy = false,
  conditional,
  configs,
  label,
  onChange,
  onRemove,
  renderLeft,
  showErrors,
}: {
  connectedBy?: boolean;
  conditional: Conditional;
  configs: QueryConditionalConfig[];
  label: string;
  onChange: (conditional: Conditional) => void;
  onRemove: () => void | null;
  renderLeft: boolean;
  showErrors: boolean;
}) => {
  const config = configs.find((c) => c.left.key === conditional.left);

  const onLeftChange = (newValue: string) => {
    const newConfig = configs.find((c) => c.left.key === newValue);
    onChange({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      left: newConfig.left.key,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      operator: Object.keys(newConfig.operatorToRight)[0] as Operator,
      right: [],
    } as Conditional);
  };
  const onOperatorChange = (newValue: Operator) => {
    // preserve the right-hand value if the config stays the same
    // if the right-hand config changes (e.g. multi=true -> multi=false), we should clear the value since the meaning might have changed or became invalid
    const rightConfigChanged = !isEqual(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      config.operatorToRight[conditional.operator],
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      config.operatorToRight[newValue],
    );

    onChange({
      left: conditional.left,
      operator: newValue,
      right: rightConfigChanged ? [] : (conditional.right as Value[]),
    } as Conditional);
  };
  const onRightChange = (v: Value[]) => {
    onChange({
      left: conditional.left,
      operator: conditional.operator,
      right: v,
    } as Conditional);
  };

  const isValid = conditional.right.length !== 0 && isConditionalValid(conditional);

  return (
    <Row>
      <LabelContainer connectedBy={connectedBy}>
        <Label>{label}</Label>
      </LabelContainer>
      {renderLeft &&
        (configs.length === 1 ? (
          // NOTE: In this case, there is only one operator option.
          <CriterionContainer connectedBy={connectedBy}>
            <Input
              type='text'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              value={config.left.display}
              readOnly={true}
              onMouseDown={(e) => e.preventDefault()}
            />
          </CriterionContainer>
        ) : (
          <CriterionContainer connectedBy={connectedBy}>
            <ReactSelect<QueryConditionalLeft>
              classNamePrefix='GraphEditorDropdown'
              value={conditional.left}
              onChange={onLeftChange}
              options={configs.map((entry) => {
                return {
                  label: entry.left.display,
                  value: entry.left.key,
                };
              })}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              styles={reactSelectStyles}
              portal
            />
          </CriterionContainer>
        ))}
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      {Object.keys(config.operatorToRight).length === 1 ? (
        /* In this case, there is only 1 allowed operator */
        <OperatorContainer>
          <Input
            type='text'
            value={operatorDisplayValue(conditional.operator, conditional.left)}
            readOnly
            onMouseDown={(e) => e.preventDefault()}
          />
        </OperatorContainer>
      ) : (
        <OperatorContainer>
          <ReactSelect
            classNamePrefix='GraphEditorDropdown'
            value={conditional.operator}
            onChange={(value) => {
              onOperatorChange(value);
            }}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            options={Object.keys(config.operatorToRight).map((operator: Operator) => ({
              value: operator,
              label: operatorDisplayValue(operator, conditional.left),
            }))}
            styles={{
              singleValue: (styles) => ({
                ...styles,
                color: '#0E0E11',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '125%',
              }),
              control: () => ({
                height: '32px',
              }),
            }}
            portal
          />
        </OperatorContainer>
      )}
      <ValueContainer canRemove={!!onRemove}>
        <FormControlPilledInput
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          controlConfig={config.operatorToRight[conditional.operator]}
          value={conditional.right as Value[]}
          hasError={showErrors && !isValid}
          onChange={onRightChange}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          placeholder={
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            config.operatorToRight[conditional.operator].type === Control.Select
              ? 'Select'
              : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                config.placeHolder
          }
          isCompact
        />
      </ValueContainer>
      {onRemove && (
        <RemoveRow role='button' aria-label='Remove filter' onClick={onRemove}>
          <RemoveRowIcon />
        </RemoveRow>
      )}
    </Row>
  );
};

export default ConditionalRow;
